import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Users, MessageSquare, Mail, Activity, ArrowRight } from "lucide-react";
import { adminApi } from "../../lib/auth";

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className={`relative overflow-hidden rounded-xl border border-white/10 bg-[#121212] p-6`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500">
          {label}
        </div>
        <div className="font-display font-bold text-4xl text-white mt-2">{value}</div>
        {sub && <div className="text-xs text-neutral-400 mt-2">{sub}</div>}
      </div>
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          accent === "blue"
            ? "bg-[#1d4ed8]/10 text-[#1d4ed8]"
            : "bg-[#dc2626]/10 text-[#dc2626]"
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#dc2626]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display font-bold text-3xl text-white tracking-tight">
          Dashboard
        </h1>
        <p className="text-neutral-400 text-sm mt-1">
          Real-time snapshot of leads, conversations and traffic.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={Users}
          label="Total Leads"
          value={stats?.leads_total ?? 0}
          sub={`${stats?.leads_today ?? 0} today · ${stats?.leads_new ?? 0} new`}
        />
        <StatCard
          icon={Activity}
          label="Site Visitors"
          value={stats?.visitors_total ?? 0}
          sub={`${stats?.visitors_today ?? 0} today`}
          accent="blue"
        />
        <StatCard
          icon={MessageSquare}
          label="Chat Messages"
          value={stats?.chats_total ?? 0}
          accent="blue"
        />
        <StatCard
          icon={Mail}
          label="Contact Forms"
          value={stats?.contacts_total ?? 0}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#dc2626]">
                Pipeline
              </div>
              <div className="font-display font-bold text-xl text-white mt-1">
                Leads by Status
              </div>
            </div>
            <Link
              to="/admin/leads"
              className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-1"
            >
              Manage <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {Object.entries(stats?.by_status || {}).map(([status, count]) => (
              <div key={status} className="flex items-center gap-4">
                <div className="text-xs uppercase font-mono tracking-[0.2em] text-neutral-400 w-24">
                  {status}
                </div>
                <div className="flex-1 h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#dc2626] to-[#1d4ed8]"
                    style={{
                      width: `${
                        stats?.leads_total ? (count / stats.leads_total) * 100 : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="font-mono text-white w-10 text-right">{count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#1d4ed8]">
            Quick actions
          </div>
          <div className="font-display font-bold text-xl text-white mt-1 mb-5">
            Where to next?
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { to: "/admin/leads", label: "Manage Leads" },
              { to: "/admin/chats", label: "Read Chats" },
              { to: "/admin/contacts", label: "Inquiries" },
              { to: "/admin/visitors", label: "Traffic" },
            ].map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="border border-white/10 rounded-md px-4 py-3 text-sm text-neutral-300 hover:text-white hover:bg-white/5 inline-flex items-center justify-between"
              >
                {q.label} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
